import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { Camera, Image as ImageIcon, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { useToast } from '../ui/use-toast';
import { apiClient, tokenStorage } from '../../utils/api-client';
import { isoToDatetimeLocal, datetimeLocalToIso } from '../../utils/datetime-local';
import {
  announcementsService,
  AnnouncementLimitError,
  type AnnouncementPayload,
} from '../../services/announcements';
import type { Announcement } from '../../types/announcement';

interface AnnouncementFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  /** When provided, the dialog edits this announcement; otherwise it creates a new one. */
  announcement?: Announcement | null;
  onSaved: (announcement: Announcement) => void;
}

export default function AnnouncementFormDialog({
  open,
  onOpenChange,
  gymSlug,
  announcement,
  onSaved,
}: AnnouncementFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [pinned, setPinned] = useState(false);
  // Optional visibility window, held as `datetime-local` values (local time).
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [imageSignedId, setImageSignedId] = useState<string | undefined>(undefined);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  // When the gym is full, we ask the user to confirm evicting the oldest announcement.
  const [replaceConfirm, setReplaceConfirm] = useState<{ open: boolean; oldestTitle?: string }>({
    open: false,
  });

  const fileInputCameraRef = useRef<HTMLInputElement>(null);
  const fileInputGalleryRef = useRef<HTMLInputElement>(null);

  // Reset the form whenever the dialog opens (prefilling in edit mode).
  useEffect(() => {
    if (!open) return;
    setTitle(announcement?.title ?? '');
    setDescription(announcement?.description ?? '');
    setLinkUrl(announcement?.link_url ?? '');
    setLinkText(announcement?.link_text ?? '');
    setPinned(announcement?.pinned ?? false);
    setStartsAt(isoToDatetimeLocal(announcement?.starts_at));
    setEndsAt(isoToDatetimeLocal(announcement?.ends_at));
    setImageSignedId(undefined);
    setImagePreviewUrl(announcement?.image_thumb_url ?? announcement?.image_url ?? null);
  }, [open, announcement]);

  const getApiOrigin = useCallback(() => {
    const baseURL = apiClient.getBaseURL();
    const fallback = {
      host: window.location.hostname,
      port: window.location.port || undefined,
      protocol: window.location.protocol.replace(':', ''),
    };
    if (!baseURL) return fallback;
    try {
      const url = new URL(baseURL);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return fallback;
    }
  }, []);

  // Keep the end on/after the start: whenever the start changes, bump the end up
  // to it when the end is empty or would fall earlier. `datetime-local` strings
  // share a fixed `yyyy-MM-ddTHH:mm` shape, so a string compare orders them.
  const handleStartsAtChange = (value: string) => {
    setStartsAt(value);
    setEndsAt(prev => (value && (!prev || prev < value) ? value : prev));
  };

  const handleSave = async (replaceOldest = false) => {
    if (!title.trim()) return;

    if (startsAt && endsAt && endsAt < startsAt) {
      toast({ title: t('announcement.endBeforeStartError'), variant: 'destructive' });
      return;
    }

    setSaving(true);

    const payload: AnnouncementPayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      link_url: linkUrl.trim() || undefined,
      link_text: linkText.trim() || undefined,
      pinned,
      starts_at: datetimeLocalToIso(startsAt),
      ends_at: datetimeLocalToIso(endsAt),
      ...(imageSignedId && { image_signed_id: imageSignedId }),
    };

    try {
      const saved = announcement
        ? await announcementsService.update(gymSlug, announcement.id, payload)
        : await announcementsService.create(gymSlug, payload, { replaceOldest });
      onSaved(saved);
      toast({ title: announcement ? t('announcement.updateSuccess') : t('announcement.createSuccess') });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof AnnouncementLimitError) {
        // Keep the form open behind a confirmation asking to evict the oldest one.
        setReplaceConfirm({ open: true, oldestTitle: err.oldestTitle });
      } else {
        toast({
          title: err instanceof Error ? err.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {announcement ? t('announcement.editTitle') : t('announcement.createTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="announcement-title">{t('announcement.title')} *</Label>
            <Input
              id="announcement-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={120}
              placeholder={t('announcement.titlePlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="announcement-description">{t('announcement.description')}</Label>
            <Textarea
              id="announcement-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="announcement-link-url">{t('announcement.linkUrl')}</Label>
              <Input
                id="announcement-link-url"
                type="url"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="announcement-link-text">{t('announcement.linkText')}</Label>
              <Input
                id="announcement-link-text"
                value={linkText}
                onChange={e => setLinkText(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="announcement-starts-at">{t('announcement.startsAt')}</Label>
              <Input
                id="announcement-starts-at"
                type="datetime-local"
                value={startsAt}
                onChange={e => handleStartsAtChange(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="announcement-ends-at">{t('announcement.endsAt')}</Label>
              <Input
                id="announcement-ends-at"
                type="datetime-local"
                value={endsAt}
                min={startsAt || undefined}
                onChange={e => setEndsAt(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t('announcement.windowHint')}</p>

          <div className="space-y-1.5">
            <Label>{t('announcement.image')}</Label>
            <div className="flex items-center gap-3">
              {imagePreviewUrl && (
                <img src={imagePreviewUrl} alt="preview" className="h-12 w-12 rounded object-cover border" />
              )}
              <DirectUploadProvider
                origin={getApiOrigin()}
                headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
                onSuccess={(signedIds: string[]) => {
                  if (!signedIds || signedIds.length === 0) {
                    setUploadingImage(false);
                    toast({ title: t('announcement.uploadError'), variant: 'destructive' });
                    return;
                  }
                  setImageSignedId(signedIds[0]);
                  setUploadingImage(false);
                }}
                render={({ handleUpload }: { handleUpload: (files: FileList) => void }) => {
                  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    setUploadingImage(true);
                    setImagePreviewUrl(URL.createObjectURL(files[0]));
                    handleUpload(files);
                    e.target.value = '';
                  };
                  return (
                    <>
                      <input ref={fileInputCameraRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={handleFileSelect} />
                      <input ref={fileInputGalleryRef} type="file" accept="image/*" className="sr-only" onChange={handleFileSelect} />
                      <Button type="button" variant="outline" size="sm" disabled={uploadingImage} onClick={() => fileInputCameraRef.current?.click()}>
                        {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="mr-1 h-4 w-4" />{t('announcement.camera')}</>}
                      </Button>
                      <Button type="button" variant="outline" size="sm" disabled={uploadingImage} onClick={() => fileInputGalleryRef.current?.click()}>
                        <ImageIcon className="mr-1 h-4 w-4" />{t('announcement.gallery')}
                      </Button>
                      {imagePreviewUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => { setImagePreviewUrl(null); setImageSignedId(undefined); }}
                          aria-label={t('announcement.removeImage')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="announcement-pinned">{t('announcement.pin')}</Label>
            <Switch id="announcement-pinned" checked={pinned} onCheckedChange={setPinned} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => handleSave()} disabled={saving || uploadingImage || !title.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('announcement.save')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Limit reached: confirm evicting the oldest announcement to make room. */}
      <AlertDialog
        open={replaceConfirm.open}
        onOpenChange={open => setReplaceConfirm(prev => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('announcement.replaceTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('announcement.replaceBody', { title: replaceConfirm.oldestTitle ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setReplaceConfirm(prev => ({ ...prev, open: false }));
                handleSave(true);
              }}
            >
              {t('announcement.replaceConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
